import { useState, useEffect, useRef } from 'react';
import { supabase, DJProfile } from '../../../lib/supabase';

interface Track {
  id: string;
  title: string;
  artist: string | null;
  cover_url: string | null;
  audio_url: string | null;
  soundcloud_url: string | null;
  duration: number | null;
  platform: string | null;
}

interface MusicPlayerProps {
  profile?: DJProfile | null;
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

type TrackType = 'audio' | 'youtube' | 'external';

function getTrackType(track: Track): TrackType {
  if (track.audio_url) return 'audio';
  const url = track.soundcloud_url || '';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  return 'external';
}

function extractYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  return match ? match[1] : null;
}

export default function MusicPlayer({ profile }: MusicPlayerProps) {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [showPlayer, setShowPlayer] = useState(false);
  const [ytReady, setYtReady] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const ytPlayerRef = useRef<any>(null);
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingYtTrackRef = useRef<Track | null>(null);
  const currentTrackRef = useRef<Track | null>(null);
  const tracksRef = useRef<Track[]>([]);

  const sectionTitle = profile?.section_music_title || 'Mis Mixes';
  const sectionDescription = profile?.section_music_description || 'Escucha mis últimas sesiones y producciones';

  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setYtReady(true);
      return;
    }
    window.onYouTubeIframeAPIReady = () => setYtReady(true);
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (ytReady && pendingYtTrackRef.current) {
      const track = pendingYtTrackRef.current;
      pendingYtTrackRef.current = null;
      initYtPlayer(track);
    }
  }, [ytReady]);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const { data, error } = await supabase
        .from('tracks')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) { setTracks([]); return; }
      setTracks(data || []);
    } catch {
      setTracks([]);
    }
  };

  // Audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      playNextInternal();
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    if (ytPlayerRef.current?.setVolume) ytPlayerRef.current.setVolume(volume * 100);
  }, [volume]);

  const startYtProgress = () => {
    stopYtProgress();
    progressIntervalRef.current = setInterval(() => {
      if (ytPlayerRef.current?.getCurrentTime) {
        setCurrentTime(ytPlayerRef.current.getCurrentTime() || 0);
        const dur = ytPlayerRef.current.getDuration?.() || 0;
        if (dur > 0) setDuration(dur);
      }
    }, 500);
  };

  const stopYtProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const playNextInternal = () => {
    const t = currentTrackRef.current;
    const list = tracksRef.current;
    if (!t || list.length === 0) return;
    const idx = list.findIndex(x => x.id === t.id);
    const next = list[(idx + 1) % list.length];
    startPlayTrack(next);
  };

  const initYtPlayer = (track: Track) => {
    const videoId = extractYouTubeId(track.soundcloud_url || '');
    if (!videoId || !ytContainerRef.current) return;

    if (ytPlayerRef.current) {
      try { ytPlayerRef.current.destroy(); } catch { /* ignore */ }
      ytPlayerRef.current = null;
    }

    const container = ytContainerRef.current;
    container.innerHTML = '';
    const div = document.createElement('div');
    const divId = 'yt-player-' + Date.now();
    div.id = divId;
    container.appendChild(div);

    ytPlayerRef.current = new window.YT.Player(divId, {
      videoId,
      playerVars: { autoplay: 1, controls: 0, rel: 0, modestbranding: 1, playsinline: 1 },
      events: {
        onReady: (e: any) => {
          e.target.setVolume(volume * 100);
          e.target.playVideo();
        },
        onStateChange: (e: any) => {
          const YT = window.YT;
          if (e.data === YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            startYtProgress();
          } else if (e.data === YT.PlayerState.PAUSED) {
            setIsPlaying(false);
            stopYtProgress();
          } else if (e.data === YT.PlayerState.ENDED) {
            setIsPlaying(false);
            stopYtProgress();
            playNextInternal();
          }
        },
      },
    });
  };

  const getPlatformInfo = (track: Track) => {
    const url = track.soundcloud_url || '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return { label: 'Ver en YouTube', icon: 'ri-youtube-line', shortLabel: 'YouTube' };
    }
    const p = track.platform || 'soundcloud';
    const map: Record<string, { label: string; icon: string; shortLabel: string }> = {
      soundcloud: { label: 'Escuchar en SoundCloud', icon: 'ri-soundcloud-line', shortLabel: 'SoundCloud' },
      spotify: { label: 'Escuchar en Spotify', icon: 'ri-spotify-line', shortLabel: 'Spotify' },
    };
    return map[p] || { label: 'Escuchar', icon: 'ri-music-line', shortLabel: 'Escuchar' };
  };

  const startPlayTrack = (track: Track) => {
    const type = getTrackType(track);

    if (audioRef.current) { audioRef.current.pause(); }
    if (ytPlayerRef.current?.pauseVideo) {
      try { ytPlayerRef.current.pauseVideo(); } catch { /* ignore */ }
    }
    stopYtProgress();

    setCurrentTrack(track);
    currentTrackRef.current = track;
    setCurrentTime(0);
    setDuration(track.duration || 0);
    setShowPlayer(true);
    setIsPlaying(false);

    if (type === 'audio') {
      if (audioRef.current) {
        audioRef.current.src = track.audio_url!;
        audioRef.current.load();
        setTimeout(() => {
          audioRef.current?.play()
            .then(() => setIsPlaying(true))
            .catch(() => {});
        }, 100);
      }
    } else if (type === 'youtube') {
      if (ytReady) {
        initYtPlayer(track);
      } else {
        pendingYtTrackRef.current = track;
      }
    } else {
      window.open(track.soundcloud_url!, '_blank', 'noopener,noreferrer');
    }
  };

  const playTrack = (track: Track) => {
    if (currentTrack?.id === track.id) {
      togglePlay();
      return;
    }
    startPlayTrack(track);
  };

  const togglePlay = () => {
    if (!currentTrack) return;
    const type = getTrackType(currentTrack);

    if (type === 'audio' && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    } else if (type === 'youtube' && ytPlayerRef.current) {
      if (isPlaying) {
        ytPlayerRef.current.pauseVideo();
      } else {
        ytPlayerRef.current.playVideo();
        startYtProgress();
      }
    }
  };

  const playNext = () => {
    if (!currentTrack || tracks.length === 0) return;
    const idx = tracks.findIndex(t => t.id === currentTrack.id);
    startPlayTrack(tracks[(idx + 1) % tracks.length]);
  };

  const playPrevious = () => {
    if (!currentTrack || tracks.length === 0) return;
    const idx = tracks.findIndex(t => t.id === currentTrack.id);
    startPlayTrack(tracks[idx === 0 ? tracks.length - 1 : idx - 1]);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (currentTrack && getTrackType(currentTrack) === 'audio' && audioRef.current) {
      audioRef.current.currentTime = time;
    } else if (ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(time, true);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const openPlatformUrl = (track: Track) => {
    if (track.soundcloud_url) window.open(track.soundcloud_url, '_blank', 'noopener,noreferrer');
  };

  const closePlayer = () => {
    if (audioRef.current) audioRef.current.pause();
    if (ytPlayerRef.current?.stopVideo) {
      try { ytPlayerRef.current.stopVideo(); } catch { /* ignore */ }
    }
    stopYtProgress();
    setShowPlayer(false);
    setIsPlaying(false);
    setCurrentTrack(null);
    currentTrackRef.current = null;
    setCurrentTime(0);
    setDuration(0);
  };

  return (
    <section id="music" className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-fuchsia-500/5"></div>

      {/* Hidden YouTube iframe container */}
      <div
        ref={ytContainerRef}
        className="fixed pointer-events-none opacity-0"
        style={{ width: 1, height: 1, bottom: 0, left: 0, zIndex: -1 }}
      />

      {/* Hidden audio element */}
      <audio ref={audioRef} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-fuchsia-500 bg-clip-text text-transparent">
            {sectionTitle}
          </h2>
          <p className="text-gray-400 text-lg">{sectionDescription}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {tracks.map((track) => {
            const isActive = currentTrack?.id === track.id;
            const type = getTrackType(track);
            return (
              <div
                key={track.id}
                className={`group bg-gradient-to-br from-gray-900 to-black border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 ${isActive ? 'border-cyan-500/60' : 'border-cyan-500/20 hover:border-cyan-500/50'}`}
              >
                <div className="relative aspect-square overflow-hidden">
                  {track.cover_url ? (
                    <img
                      src={track.cover_url}
                      alt={track.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 flex items-center justify-center">
                      <i className="ri-music-2-line text-4xl md:text-6xl text-cyan-400"></i>
                    </div>
                  )}

                  {/* Playing indicator bars */}
                  {isActive && isPlaying && (
                    <div className="absolute top-2 left-2 flex items-end gap-0.5 h-5">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="w-1 bg-cyan-400 rounded-full"
                          style={{
                            height: '100%',
                            animation: `equalizerBar${i} 0.8s ease-in-out infinite alternate`,
                            animationDelay: `${i * 0.1}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <button
                      onClick={() => playTrack(track)}
                      className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300 hover:scale-110 cursor-pointer"
                    >
                      <i className={`${isActive && isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-xl md:text-3xl text-white`}></i>
                    </button>
                  </div>

                  {/* Mobile: overlay title */}
                  <div className="md:hidden absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-2">
                    <h3 className="text-xs font-bold text-white truncate">{track.title}</h3>
                    {track.artist && <p className="text-cyan-400 text-[10px] truncate">{track.artist}</p>}
                  </div>
                </div>

                {/* Desktop info */}
                <div className="hidden md:block p-5">
                  <h3 className="text-xl font-bold text-white mb-1 truncate">{track.title}</h3>
                  {track.artist && <p className="text-cyan-400 text-sm mb-3">{track.artist}</p>}
                  {track.duration && (
                    <p className="text-gray-500 text-sm">
                      <i className="ri-time-line mr-1"></i>
                      {formatTime(track.duration)}
                    </p>
                  )}
                  <div className="mt-4 flex flex-col gap-2">
                    {(type === 'audio' || type === 'youtube') && (
                      <button
                        onClick={() => playTrack(track)}
                        className="w-full py-2 px-4 bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border border-cyan-500/40 rounded-lg text-white hover:border-cyan-400 transition-all duration-300 whitespace-nowrap cursor-pointer font-medium"
                      >
                        <i className={`${isActive && isPlaying ? 'ri-pause-circle-line' : 'ri-play-circle-line'} mr-2`}></i>
                        {isActive && isPlaying ? 'Pausar' : 'Reproducir'}
                      </button>
                    )}
                    {track.soundcloud_url && (
                      <button
                        onClick={() => openPlatformUrl(track)}
                        className="w-full py-2 px-4 bg-transparent border border-cyan-500/20 rounded-lg text-cyan-400 hover:border-cyan-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                      >
                        <i className={`${getPlatformInfo(track).icon} mr-2`}></i>
                        {getPlatformInfo(track).label}
                      </button>
                    )}
                    {type === 'external' && !track.audio_url && (
                      <button
                        disabled
                        className="w-full py-2 px-4 bg-transparent border border-gray-700 rounded-lg text-gray-600 whitespace-nowrap cursor-not-allowed text-sm"
                      >
                        <i className="ri-music-line mr-2"></i>
                        Sin audio disponible
                      </button>
                    )}
                  </div>
                </div>

                {/* Mobile buttons */}
                <div className="md:hidden p-2 flex gap-1">
                  {(type === 'audio' || type === 'youtube') && (
                    <button
                      onClick={() => playTrack(track)}
                      className="flex-1 py-1.5 bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border border-cyan-500/30 rounded-md text-white text-xs whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${isActive && isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} mr-1`}></i>
                      {isActive && isPlaying ? 'Pausa' : 'Play'}
                    </button>
                  )}
                  {track.soundcloud_url && (
                    <button
                      onClick={() => openPlatformUrl(track)}
                      className="flex-1 py-1.5 bg-transparent border border-cyan-500/20 rounded-md text-cyan-400 text-xs whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${getPlatformInfo(track).icon} mr-1`}></i>
                      {getPlatformInfo(track).shortLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {tracks.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-music-2-line text-6xl text-gray-700 mb-4"></i>
            <p className="text-gray-500 text-lg">No hay tracks disponibles</p>
          </div>
        )}
      </div>

      {/* ===== REPRODUCTOR FIJO INFERIOR ===== */}
      {showPlayer && currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          {/* Barra de progreso encima */}
          <div className="w-full h-1 bg-gray-800 relative cursor-pointer group/bar">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 transition-all duration-300"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            />
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="absolute inset-0 w-full opacity-0 cursor-pointer h-full"
            />
          </div>

          {/* Cuerpo del reproductor */}
          <div className="bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 px-4 md:px-8 py-3 md:py-4">
            <div className="max-w-7xl mx-auto flex items-center gap-4 md:gap-8">

              {/* Cover + info */}
              <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-72">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden flex-shrink-0">
                  {currentTrack.cover_url ? (
                    <img src={currentTrack.cover_url} alt={currentTrack.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 flex items-center justify-center">
                      <i className="ri-music-2-line text-lg text-cyan-400"></i>
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{currentTrack.title}</p>
                  {currentTrack.artist && (
                    <p className="text-cyan-400 text-xs truncate">{currentTrack.artist}</p>
                  )}
                </div>
              </div>

              {/* Controles centrales */}
              <div className="flex items-center gap-3 md:gap-5 flex-shrink-0 mx-auto">
                <button
                  onClick={playPrevious}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-skip-back-fill text-lg md:text-xl"></i>
                </button>

                <button
                  onClick={togglePlay}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full hover:scale-110 transition-transform cursor-pointer"
                >
                  <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-lg md:text-xl text-white`}></i>
                </button>

                <button
                  onClick={playNext}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-skip-forward-fill text-lg md:text-xl"></i>
                </button>
              </div>

              {/* Tiempo + barra (desktop) */}
              <div className="hidden md:flex items-center gap-3 flex-1">
                <span className="text-xs text-gray-500 w-10 text-right tabular-nums">{formatTime(currentTime)}</span>
                <div className="flex-1 relative h-1 bg-gray-700 rounded-full cursor-pointer">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  />
                </div>
                <span className="text-xs text-gray-500 w-10 tabular-nums">{formatTime(duration)}</span>
              </div>

              {/* Volumen + cerrar (desktop) */}
              <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <i className="ri-volume-up-line text-gray-400 text-sm"></i>
                  <div className="relative w-20 h-1 bg-gray-700 rounded-full cursor-pointer">
                    <div
                      className="h-full bg-cyan-400 rounded-full"
                      style={{ width: `${volume * 100}%` }}
                    />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      className="absolute inset-0 w-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                <button
                  onClick={closePlayer}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Cerrar en móvil */}
              <button
                onClick={closePlayer}
                className="md:hidden w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes equalizerBar1 { from { height: 30% } to { height: 100% } }
        @keyframes equalizerBar2 { from { height: 60% } to { height: 40% } }
        @keyframes equalizerBar3 { from { height: 80% } to { height: 20% } }
        @keyframes equalizerBar4 { from { height: 20% } to { height: 90% } }
      `}</style>
    </section>
  );
}
